import React from 'react'
import { useMetaContext } from '../../providers/MetaProvider'
import { useEditContext } from '../../providers/EditProvider'
import isEqual from 'lodash.isequal'
import { isUUID } from '../../utilities'

const useObjectChanged = () => {
  // keeps track of edited meta
  const [initial, setInitial] = React.useState({})
  const [set, isSet] = React.useState(false)
  const [afterSet, setAfterSet] = React.useState({})

  // keeps track of auth
  const [auth, setAuth] = React.useState({})
  const [authSet, isAuthSet] = React.useState(false)
  const [editedAuthObj, setEditedAuthObj] = React.useState({})

  const { setEditedMeta, setEditedAuth } = useEditContext()
  const { meta } = useMetaContext()

  React.useEffect(() => {
    const newObj = {}
    let metaObj = {}

    const vals = Object.values(initial).filter(v => v?.length ?? 0)
    // unblocking meta that is empty
    if (vals.length > 1) {
      setEditedMeta(true)
    }

    // convert initial to object and check if it's the same as meta
    for (const key in initial) {
      if (initial[key] && initial[key]?.length) {
        newObj[key] = initial[key]
      }
    }

    delete newObj.tags

    if (meta?.length) {
      setEditedMeta(false)
      // convert meta into object
      meta.forEach(m => {
        metaObj[m.key] = m.value
      })

      // setting initial object and loaded state
      if (Object.keys(newObj).length && Object.keys(metaObj).length) {
        if (newObj.tags && metaObj.tags) {
          delete newObj.tags
          delete metaObj.tags
        }

        // external users don't have name property in meta
        if (!metaObj.name) {
          delete newObj.name
        }

        const equal = isEqual(newObj, metaObj)

        if (equal && !set) {
          isSet(true)
          setInitial(initial)
        }
      }
    }
  }, [meta, initial, set, setEditedMeta])

  React.useEffect(() => {
    if (set) {
      if (Object.keys(initial).length && Object.keys(afterSet).length) {
        const withoutIDsAfter = {}
        Object.entries(afterSet).forEach(([key, value]) => {
          if (!isUUID(key)) {
            withoutIDsAfter[key] = value
          }
        })
        const withoutIDsInitial = {}
        Object.entries(initial).forEach(([key, value]) => {
          if (!isUUID(key)) {
            withoutIDsInitial[key] = value
          }
        })

        const equal = isEqual(withoutIDsInitial, withoutIDsAfter)

        if (equal) {
          setEditedMeta(false)
        } else {
          setEditedMeta(true)
        }
      }
    }
  }, [initial, set, afterSet, setEditedMeta])

  React.useEffect(() => {
    if (auth.exists && !authSet) {
      if (Object.keys(auth.details).length) {
        setAuth(auth => ({
          ...auth.details,
          suspended_at: auth.details.suspended_at ? true : false
        }))
      } else {
        setAuth(auth => ({
          ...auth.details
        }))
      }

      isAuthSet(true)
    }
  }, [auth, authSet, setEditedAuth])

  React.useEffect(() => {
    if (!authSet || !auth) {
      return
    }

    const extracted = auth.details ? auth.details : auth

    const formatted = { ...editedAuthObj }

    // times in here will never be equal, need to transform into boolean
    formatted.suspended_at
      ? (formatted.suspended_at = true)
      : (formatted.suspended_at = false)

    if (authSet) {
      if (Object.keys(extracted).length && Object.keys(editedAuthObj).length) {
        const equal = isEqual(extracted, formatted)

        if (equal) {
          setEditedAuth(false)
        } else {
          setEditedAuth(true)
        }
      } else if (
        !Object.keys(extracted).length &&
        Object.keys(editedAuthObj).length
      ) {
        // case for emptying fields
        if (
          editedAuthObj.suspended_at !== false &&
          !editedAuthObj.email?.length
        ) {
          setEditedAuth(false)
        } else {
          setEditedAuth(true)
        }
      }
    }
  }, [authSet, editedAuthObj, auth, setEditedAuth])

  return {
    initial,
    setInitial,
    set,
    setAfterSet,
    setAuth,
    editedAuthObj,
    setEditedAuthObj,
    authSet,
    isSet,
    isAuthSet
  }
}

export default useObjectChanged
